import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { message, Select, Button, Drawer, Spin } from "antd"
import { CheckCircle, Clock, XCircle, ArrowUp, ArrowDown } from "lucide-react"
import { PlusOutlined } from "@ant-design/icons"
import { setAllLeaves, setLeaveTotal, setLeavePage, setLeavePageSize, } from "../../Redux/actions/myLeaveAction"
import { GET } from "../../utils/apis"
import { secureApi } from "../../Config/api"
import ApplyLeave from "../ApplyLeaves/ApplyLeaves"
import { translations } from "../../utils/constants"
import { convertItalianToLocalTime, verifyToken } from "../../utils/helpers"
import moment from "moment"
import paths from "../../Config/paths"
import { socket } from "../../Config/socket"

const MyLeaves = ({ language }) => {
    const dispatch = useDispatch()
    const history = useHistory()

    const user = useSelector((state) => state.authReducer?.user)
    const { leaves, page, pageSize, total } = useSelector(
        (state) => state.leaveReducer
    )
    const userId = user?.id
    const [loading, setLoading] = useState(false)
    const [leaveDrawerVisible, setLeaveDrawerVisible] = useState(false)

    useEffect(() => {
        verifyToken(history, dispatch)
    }, [])

    useEffect(() => {
        if (!user) {
            history.push(paths?.HOME)
        }
    }, [user, history]);

    const fetchLeaves = async () => {
        try {
            setLoading(true)
            const params = { user_id: userId, page, limit: pageSize }
            const response = await secureApi?.get(GET?.GET_LEAVE, { params })
            if (response?.success) {
                const mapped = response?.data?.map((l) => ({
                    key: l.id,
                    ...l,
                }))
                dispatch(setAllLeaves(mapped))
                dispatch(
                    setLeaveTotal(response?.pagination?.total || mapped.length)
                )
            } else {
                message.error(
                    response?.message || translations[language]?.fetchLeavesError
                )
            }
        } catch (err) {
            message.error(translations[language]?.fetchLeavesError)
        } finally {
            setLoading(false)
        }
    }

    const fetchLeavesWithoutLoading = async () => {
        try {
            const params = { user_id: userId, page, limit: pageSize }
            const response = await secureApi?.get(GET?.GET_LEAVE, { params })
            if (response?.success) {
                const mapped = response?.data?.map((l) => ({
                    key: l.id,
                    ...l,
                }))
                dispatch(setAllLeaves(mapped))
                dispatch(
                    setLeaveTotal(response?.pagination?.total || mapped.length)
                )
            } else {
                message.error(
                    response?.message || translations[language]?.fetchLeavesError
                )
            }
        } catch (err) {
            message.error(translations[language]?.fetchLeavesError)
        } finally {
            setLoading(false)
        }
    }



    useEffect(() => {
        socket.on("leaves:changed", (payload) => {
            console.log("Realtime payload:", payload);
            if (user?.role === "user") {
                if (payload?.user_id === user?.id) {
                    fetchLeavesWithoutLoading()
                }
            } else {
                fetchLeavesWithoutLoading()
            }
        });

        return () => {
            socket.off("leaves:changed");
        };
    }, []);




    useEffect(() => {
        if (userId) {
            fetchLeaves()
        }
    }, [userId, page, pageSize])

    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString("en-GB")
    }

    const formatDateRange = (record) => {
        if (record.type === "vacation") {
            return `${formatDate(record.start_date)} - ${formatDate(record.end_date)}`
        } else {
            let base = formatDate(record.leave_date)
            if (!record.full_day && record.start_time && record.end_time) {
                try {
                    const startLocal = convertItalianToLocalTime(record.start_time)
                    const endLocal = convertItalianToLocalTime(record.end_time)
                    const formattedStart = moment(startLocal).format("HH:mm")
                    const formattedEnd = moment(endLocal).format("HH:mm")

                    base += ` (${formattedStart} - ${formattedEnd})`
                } catch (err) {
                    console.error("Time conversion error:", err)
                    base += ` (${record.start_time} - ${record.end_time})`
                }
            } else {
                base += ` ${translations[language]?.fullDay}`
            }

            return base
        }
    }

    const getStatusClass = (status) => {
        switch (status) {
            case "approved":
                return "status-approved"
            case "pending":
                return "status-pending"
            case "rejected":
                return "status-rejected"
            default:
                return "status-unknown"
        }
    }

    const getStatusText = (status) => {
        switch (status) {
            case "approved":
                return translations[language]?.approved
            case "pending":
                return translations[language]?.pending
            case "rejected":
                return translations[language]?.rejected
            default:
                return translations[language]?.unknown
        }
    }

    const handlePageChange = (newPage) => {
        dispatch(setLeavePage(newPage))
    }

    const handlePageSizeChange = (size) => {
        dispatch(setLeavePageSize(size))
        dispatch(setLeavePage(1))
    }

    const TrendIndicator = ({ trend, isPositive }) => (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                color: isPositive ? "#28a745" : "#dc3545",
                fontWeight: 500,
            }}
        >
            {isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
            <span>{Math.abs(trend)}%</span>
        </div>
    )

    const dashboardCards = [
        {
            title: translations[language]?.approvedLeaves,
            value: leaves.filter((l) => l.is_approved === "approved").length,
            icon: (
                <div
                    style={{
                        background: "rgba(40, 167, 69, 0.15)",
                        borderRadius: "50%",
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CheckCircle size={22} color="#28a745" />
                </div>
            ),
            trend: { value: 5.2, isPositive: true },
            period: translations[language]?.thisMonth,
        },
        {
            title: translations[language]?.pendingLeaves,
            value: leaves.filter((l) => l.is_approved === "pending").length,
            icon: (
                <div
                    style={{
                        background: "rgba(250, 173, 20, 0.15)",
                        borderRadius: "50%",
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Clock size={22} color="#faad14" />
                </div>
            ),
            trend: { value: -3.1, isPositive: false },
            period: translations[language]?.thisMonth,
        },
        {
            title: translations[language]?.rejectedLeaves,
            value: leaves.filter((l) => l.is_approved === "rejected").length,
            icon: (
                <div
                    style={{
                        background: "rgba(255, 77, 79, 0.15)",
                        borderRadius: "50%",
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <XCircle size={22} color="#ff4d4f" />
                </div>
            ),
            trend: { value: 1.8, isPositive: false },
            period: translations[language]?.thisMonth,
        },
    ]

    return (
        <div className="home-container" style={{ padding: 24 }}>
            <div
                className="my-leaves-header"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <div className="header-content">
                    <h1 className="page-title">
                        {translations[language]?.myLeaveDashboard}
                    </h1>
                    <p className="page-subtitle">
                        {translations[language]?.myLeaveSubtitle}
                    </p>
                </div>
                <Button
                    className="export-btn apply-for-leave-btn"
                    type="primary"
                    onClick={() => setLeaveDrawerVisible(true)}
                    icon={<PlusOutlined />}
                >
                    {translations[language]?.applyForLeave}
                </Button>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 16,
                    marginBottom: 32,
                }}
            >
                {dashboardCards?.map((card, index) => (
                    <div key={index} className="dashboard-card">
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 8,
                            }}
                        >
                            {card.icon}
                            <TrendIndicator
                                trend={card.trend.value}
                                isPositive={card.trend.isPositive}
                            />
                        </div>
                        <h3 style={{ margin: "8px 0", fontSize: "15px" }}>
                            {card.title}
                        </h3>
                        <p style={{ fontSize: 18, fontWeight: 600 }}>
                            {card?.value}
                        </p>
                        <div style={{ marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: "#999" }}>
                                {card?.period}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="leaves-content">
                <Spin spinning={loading}>
                    <div className="leaves-grid">
                        {leaves?.map((leave) => (
                            <div key={leave.id} className="leave-card">
                                <div className="leave-card-header">
                                    <div className="leave-type">
                                        <span className="type-badge">
                                            {translations[language]?.[leave.type]}
                                        </span>
                                    </div>
                                    <div
                                        className={`status-badge ${getStatusClass(
                                            leave.is_approved
                                        )}`}
                                    >
                                        {getStatusText(leave.is_approved)}
                                    </div>
                                </div>

                                <div className="leave-card-body">
                                    <div className="leave-reason">
                                        <h3>{leave.reason}</h3>
                                    </div>

                                    <div className="leave-details">
                                        <div className="detail-item">
                                            <span className="detail-label">
                                                {translations[language]?.dates}
                                            </span>
                                            <span className="detail-value">
                                                {formatDateRange(leave)}
                                            </span>
                                        </div>

                                        <div className="detail-item">
                                            <span className="detail-label">
                                                {translations[language]?.appliedOn}
                                            </span>
                                            <span className="detail-value">
                                                {formatDate(leave.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {leaves.length === 0 && !loading && (
                        <div className="empty-state">
                            <div className="empty-icon">📋</div>
                            <h3>{translations[language]?.noLeaveRecords}</h3>
                            <p>{translations[language]?.noLeaveSubtitle}</p>
                        </div>
                    )}
                </Spin>

                <div className="pagination">
                    <div className="pagination-info">
                        {translations[language]?.paginationInfo
                            .replace("{from}", (page - 1) * pageSize + 1)
                            .replace("{to}", Math.min(page * pageSize, total))
                            .replace("{total}", total)}
                    </div>
                    <div className="pagination-controls">
                        <Select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            className="antd-select"
                            style={{ width: 120 }}
                            options={[
                                {
                                    value: 5,
                                    label: translations[language]?.perPage.replace(
                                        "{count}",
                                        5
                                    ),
                                },
                                {
                                    value: 10,
                                    label: translations[language]?.perPage.replace(
                                        "{count}",
                                        10
                                    ),
                                },
                                {
                                    value: 20,
                                    label: translations[language]?.perPage.replace(
                                        "{count}",
                                        20
                                    ),
                                },
                            ]}
                        />
                        <div className="page-buttons">
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page === 1}
                                className="page-btn"
                            >
                                {translations[language]?.previous}
                            </button>
                            <span className="page-info">
                                {translations[language]?.pageInfo
                                    .replace("{page}", page)
                                    .replace("{pages}", Math.ceil(total / pageSize))}
                            </span>
                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= Math.ceil(total / pageSize)}
                                className="page-btn"
                            >
                                {translations[language]?.next}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Drawer
                title={translations[language]?.applyForLeave}
                width={720}
                onClose={() => setLeaveDrawerVisible(false)}
                open={leaveDrawerVisible}
                destroyOnClose
                placement="right"
            >
                <ApplyLeave
                    language={language}
                    onSuccess={() => {
                        setLeaveDrawerVisible(false)
                        fetchLeaves()
                    }}
                    onCancel={() => setLeaveDrawerVisible(false)}
                />
            </Drawer>
        </div>
    )
}

export default MyLeaves